import * as React from 'react';
import {
  Body,
  Button,
  Input,
  LoadingIcon,
  Modal,
  Selector,
  Tooltip,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  ADD_SSO_ENDPOINT_DIALOG_CANCEL,
  ADD_SSO_ENDPOINT_DIALOG_ENDPOINT,
  ADD_SSO_ENDPOINT_DIALOG_INTERNAL_ENDPOINT,
  ADD_SSO_ENDPOINT_DIALOG_NAME,
  ADD_SSO_ENDPOINT_DIALOG_REGISTRATION,
  ADD_SSO_ENDPOINT_DIALOG_REGISTRATION_ITEM,
  ADD_SSO_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  SsoConnectionRegistration,
  useAddSsoConnectionMutation,
  useGetSsoConnectionRegistryQuery,
} from 'src/ui/graph/types';
import { useOnError } from 'src/ui/hooks/graph';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';
import { redirectToUrl } from 'src/ui/utils/window';

const isOpenSelector = isDialogOpenSelector(DialogType.ADD_SSO_ENDPOINT);

export const AddSsoEndpointDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const onError = useOnError();

  const isOpen = useAppSelector(isOpenSelector);
  const didIsOpenChange = useDidValueChange(isOpen);

  const {
    data: registryData,
    isLoading: isRegistryLoading,
  } = useGetSsoConnectionRegistryQuery(graphQLClient, {}, { enabled: isOpen });

  const {
    isLoading: isAdding,
    mutate: addSsoConnection,
  } = useAddSsoConnectionMutation(graphQLClient, {
    onError,
    onSuccess: (data) => {
      handleClose();
      if (data?.addSSOConnection?.redirect_url) {
        redirectToUrl(data.addSSOConnection.redirect_url);
      }
    },
  });

  const [name, setName] = React.useState('');
  const [endpoint, setEndpoint] = React.useState('');
  const [internalEndpoint, setInternalEndpoint] = React.useState('');
  const [selectedProviderName, setSelectedProviderName] = React.useState('');

  const registrations = React.useMemo(() => {
    return (registryData?.getSSOConnectionRegistry || []).filter(
      (r): r is SsoConnectionRegistration => r !== null
    );
  }, [registryData]);

  const registrationItems = React.useMemo<SelectItem[]>(() => {
    return registrations.map((r) => ({
      value: r.provider_name,
      text: r.display_provider_name,
    }));
  }, [registrations]);

  const selectedRegistration = React.useMemo(() => {
    return registrations.find((r) => r.provider_name === selectedProviderName);
  }, [registrations, selectedProviderName]);

  React.useEffect(() => {
    if (!didIsOpenChange || !isOpen) {
      return;
    }

    setName('');
    setEndpoint('');
    setInternalEndpoint('');
    setSelectedProviderName('');
  }, [didIsOpenChange, isOpen]);

  const handleClose = () => {
    dispatch(closedDialog());
    setName('');
    setEndpoint('');
    setInternalEndpoint('');
    setSelectedProviderName('');
  };

  const handleSelectRegistration = (item: SelectItem | null) => {
    if (!item) {
      setSelectedProviderName('');
      return;
    }

    setSelectedProviderName(item.value);

    const registration = registrations.find(
      (r) => r.provider_name === item.value
    );
    if (registration?.stardog_endpoint) {
      setEndpoint(registration.stardog_endpoint);
    }
    if (registration?.stardog_internal_endpoint) {
      setInternalEndpoint(registration.stardog_internal_endpoint);
    }
  };

  const handleSubmit = () => {
    const trimmedEndpoint = endpoint.trim();
    const trimmedName = name.trim();

    if (!trimmedEndpoint || !trimmedName || !selectedRegistration) {
      return;
    }

    addSsoConnection({
      input: {
        connection_name: trimmedName,
        stardog_endpoint: trimmedEndpoint,
        internalEndpoint: internalEndpoint.trim() || undefined,
        provider_name: selectedRegistration?.provider_name ?? '',
      },
    });
  };

  let submitTooltip = '';
  if (!selectedRegistration) {
    submitTooltip =
      copy.components.endpoints.inputs.ssoRegistration.validationRequired;
  } else if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  } else if (!endpoint.trim().startsWith('http')) {
    submitTooltip = copy.components.endpoints.inputs.endpoint.validationHttp;
  }

  const selectedItem =
    registrationItems.find((item) => item.value === selectedProviderName) ||
    null;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={ADD_SSO_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.addEndpointDialog.buttons.cancel}
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isRegistryLoading || isAdding || Boolean(submitTooltip)}
              leftIcon={isRegistryLoading || isAdding ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={ADD_SSO_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.endpoints.addSSOEndpointDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.endpoints.addSSOEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <div className="endpoint-dialog-container">
          <Body size="md">
            {
              copy.components.endpoints.addSSOEndpointDialog.description
                .overview
            }
          </Body>
          <Body size="md">
            {
              copy.components.endpoints.addSSOEndpointDialog.description
                .redirect
            }
          </Body>
        </div>

        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <div className="endpoint-dialog-container">
            <Selector.Single<null, SelectItem, SelectItem | null>
              filterable
              inputProps={{
                className: 'endpoint-dialog-chip-selector',
                label: copy.components.endpoints.inputs.ssoRegistration.label,
                required: true,
                size: 'md',
                testId: ADD_SSO_ENDPOINT_DIALOG_REGISTRATION,
                value: selectedRegistration?.display_provider_name,
              }}
              items={registrationItems}
              itemTestId={ADD_SSO_ENDPOINT_DIALOG_REGISTRATION_ITEM}
              loading={isRegistryLoading}
              onSelect={handleSelectRegistration}
              popoverProps={{
                portalClassName: 'endpoint-dialog-menu-portal',
              }}
              selected={selectedItem}
            />
            <Body className="endpoint-dialog-input-footer" size="sm">
              {copy.components.endpoints.inputs.ssoRegistration.help}
            </Body>
          </div>

          <Input
            clearable={false}
            id="sd-sso-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required
            size="md"
            testId={ADD_SSO_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <Input
            clearable={false}
            id="sd-sso-connection-endpoint"
            label={copy.components.endpoints.inputs.endpoint.label}
            onChange={(e) => setEndpoint(e.target.value)}
            required
            size="md"
            testId={ADD_SSO_ENDPOINT_DIALOG_ENDPOINT}
            value={endpoint}
          />

          <div className="endpoint-dialog-divider" />

          <div className="endpoint-dialog-container">
            <Input
              clearable={false}
              id="sd-sso-connection-internal-endpoint"
              label={copy.components.endpoints.inputs.internalEndpoint.label}
              onChange={(e) => setInternalEndpoint(e.target.value)}
              placeholder={
                copy.components.endpoints.inputs.internalEndpoint.placeholder
              }
              size="md"
              testId={ADD_SSO_ENDPOINT_DIALOG_INTERNAL_ENDPOINT}
              value={internalEndpoint}
            />
            <Body className="endpoint-dialog-input-footer" size="sm">
              {copy.components.endpoints.inputs.internalEndpoint.help}
            </Body>
          </div>

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
